import { useState, useEffect, Fragment } from 'react';
import NetworkGraph from './NetworkGraph';
import ContactList from './ContactList';
import OpportunityPipeline from './OpportunityPipeline';
import GovLandscape from './GovLandscape';
import CompetitorView from './CompetitorView';
import FundingWatch from './FundingWatch';
import VerticalCoverage from './VerticalCoverage';
import GrowthPipeline from './GrowthPipeline';
import SupplyChain from './SupplyChain';
import KnowledgeBase from './KnowledgeBase';
import CRMSettings from './CRMSettings';
import WateringHole from './WateringHole';
import TechDevelopment from './TechDevelopment';
import TheFamily from './TheFamily';
import PersonalInbox from './HQInbox'; // Component renamed to PersonalInbox for broader access
import WelcomeSnapshot, { shouldShowWelcome } from './WelcomeSnapshot';
import GovernmentOpportunities from './GovernmentOpportunities';
import InsertGovOpportunity from './InsertGovOpportunity';
import BDCalendar from './BDCalendar';
import { useIsHQ } from '../../hooks/useIsHQ';

// Elephant-themed navigation: theme at the top level, function in the sub-nav.
const GROUPS = [
  {
    key: 'home', label: 'Watering Hole', hqOnly: false,
    title: 'Daily snapshot — pipeline, actions, deal flow, and gaps',
    views: [
      { key: 'home', label: 'Snapshot' },
      { key: 'inbox', label: 'Inbox' },
    ],
  },
  {
    key: 'family', label: 'The Family', hqOnly: false,
    title: 'The team — who we are and what we are building toward',
    views: [
      { key: 'family', label: 'The Family' },
      { key: 'growth', label: 'Growth', hqOnly: true, section: 'The Matriarch' },
    ],
  },
  {
    key: 'herd', label: 'The Herd', hqOnly: false,
    title: 'Relationships — contacts and the network that connects them',
    views: [{ key: 'contacts', label: 'Contacts' }, { key: 'network', label: 'Network Graph' }],
  },
  {
    key: 'savanna', label: 'The Savanna', hqOnly: false,
    title: 'The terrain — government landscape, market intelligence, and capability footprint',
    views: [
      { key: 'landscape', label: 'Gov Landscape' },
      { key: 'supply-chain', label: 'Supply Chain' },
      { key: 'competitors', label: 'Industry' },
      { key: 'events', label: 'Events' },
      { key: 'coverage', label: 'Our Territory' },
    ],
  },
  {
    key: 'charge', label: 'The Charge', hqOnly: false,
    title: 'Execution — opportunity pipeline, tech development, and market engagement',
    views: [
      { key: 'pipeline', label: 'Pipeline' },
      { key: 'calendar', label: 'Calendar' },
      { key: 'tech-dev', label: 'Tusks' },
      { key: 'funding-watch', label: 'Deal Flow' },
      { key: 'gov-opportunities', label: 'Government Opportunities' },
      { key: 'insert-opportunity', label: '+ Add Opportunity' },
    ],
  },
  {
    key: 'memory', label: 'Memory', hqOnly: false,
    title: 'Reference — glossary, verticals, acronym finder',
    views: [{ key: 'knowledge', label: 'Knowledge Base' }],
  },
];

const groupOf = (viewKey) => GROUPS.find(g => g.views.some(v => v.key === viewKey)) || GROUPS[0];

export default function CRMDashboard() {
  const isHQ = useIsHQ();
  const [view, setView] = useState('home');
  const [showSettings, setShowSettings] = useState(false);
  const [settingsSection, setSettingsSection] = useState('integrations');
  const [showWelcome, setShowWelcome] = useState(() => shouldShowWelcome());

  // Handle Outlook callback params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('outlook_connected') === '1' || params.get('outlook_error')) {
      setSettingsSection('integrations');
      setShowSettings(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Token self-heal: roles are frozen into tokens at login; re-mint from current
  // DB state so promotions (e.g. to headquarters) apply without re-login.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    let payload = null;
    try { payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))); } catch { return; }
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/auth/refresh`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (!d?.token) return;
        localStorage.setItem('token', d.token);
        if (payload && d.role !== payload.role) window.location.reload();
      })
      .catch(() => {});
  }, []);

  const activeGroup = groupOf(view);

  const navigate = (viewKey) => {
    setView(viewKey);
    setShowSettings(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#0A0F1C' }}>
      {/* Group nav */}
      <div style={{
        display: 'flex', gap: 0, borderBottom: '1px solid #1E2D45',
        backgroundColor: '#0A0F1C', flexShrink: 0, alignItems: 'center', overflowX: 'auto',
      }}>
        <div style={{ flex: 1, display: 'flex', overflowX: 'auto', minWidth: 0 }}>
          {GROUPS.filter(g => (!g.hqOnly || isHQ) && g.views.some(v => !v.hqOnly || isHQ)).map(g => {
            const active = !showSettings && activeGroup.key === g.key;
            const firstVisible = g.views.find(v => !v.hqOnly || isHQ);
            return (
              <button key={g.key} title={g.title} onClick={() => navigate(firstVisible.key)} style={{
                padding: 'max(6px, 10px) max(12px, 18px)', background: 'none', border: 'none',
                borderBottom: active ? '2px solid #22D3EE' : '2px solid transparent',
                color: active ? '#22D3EE' : '#6B7280', cursor: 'pointer',
                fontSize: 'clamp(11px, 2vw, 12px)', fontWeight: active ? '600' : '400',
                whiteSpace: 'nowrap', flexShrink: 0, letterSpacing: '0.02em',
              }}>
                {g.label}
              </button>
            );
          })}
        </div>
        <button
          onClick={() => setShowSettings(s => !s)}
          title="Settings"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: showSettings ? '#22D3EE' : '#6B7280',
            padding: '10px 14px', fontSize: 16, lineHeight: 1, flexShrink: 0,
            borderBottom: showSettings ? '2px solid #22D3EE' : '2px solid transparent',
          }}
        >
          ⚙
        </button>
      </div>

      {/* Sub-nav (only when the active group holds more than one visible view) */}
      {!showSettings && activeGroup.views.filter(v => !v.hqOnly || isHQ).length > 1 && (
        <div style={{ display: 'flex', borderBottom: '1px solid #1E2D45', backgroundColor: '#0D1526', flexShrink: 0, alignItems: 'center' }}>
          {activeGroup.views.filter(v => !v.hqOnly || isHQ).map((v, i, arr) => (
            <Fragment key={v.key}>
              {v.section && (i === 0 || arr[i - 1].section !== v.section) && (
                <span style={{
                  color: '#c4a563', fontSize: '9px', fontWeight: 700, letterSpacing: '0.1em',
                  textTransform: 'uppercase', padding: '0 8px 0 16px', borderLeft: '1px solid #1E2D45',
                  whiteSpace: 'nowrap',
                }}>
                  {v.section}
                </span>
              )}
              <button onClick={() => setView(v.key)} style={{
                padding: '7px 18px', background: 'none', border: 'none',
                borderBottom: view === v.key ? '2px solid #22D3EE' : '2px solid transparent',
                color: view === v.key ? '#94E8F7' : '#4B5563', cursor: 'pointer',
                fontSize: '11px', fontWeight: view === v.key ? '600' : '400', whiteSpace: 'nowrap',
              }}>
                {v.label}
              </button>
            </Fragment>
          ))}
        </div>
      )}

      {/* Content */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {showSettings && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <CRMSettings initialSection={settingsSection} />
          </div>
        )}

        {!showSettings && view === 'home' && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <WateringHole onNavigate={navigate} />
          </div>
        )}

        {!showSettings && view === 'knowledge' && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <KnowledgeBase />
          </div>
        )}

        {!showSettings && view === 'coverage' && (
          <VerticalCoverage />
        )}

        {!showSettings && view === 'supply-chain' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px' }}>
            <SupplyChain />
          </div>
        )}

        {!showSettings && view === 'growth' && isHQ && (
          <GrowthPipeline />
        )}

        {!showSettings && view === 'tech-dev' && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <TechDevelopment onNavigate={navigate} />
          </div>
        )}

        {!showSettings && view === 'family' && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <TheFamily />
          </div>
        )}

        {!showSettings && view === 'inbox' && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <PersonalInbox />
          </div>
        )}

        {!showSettings && view === 'network' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <NetworkGraph />
          </div>
        )}

        {!showSettings && view === 'contacts' && (
          <ContactList />
        )}

        {!showSettings && view === 'landscape' && (
          <GovLandscape />
        )}

        {!showSettings && view === 'pipeline' && (
          <OpportunityPipeline />
        )}

        {!showSettings && view === 'competitors' && (
          <CompetitorView />
        )}

        {!showSettings && view === 'funding-watch' && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <FundingWatch />
          </div>
        )}

        {!showSettings && view === 'gov-opportunities' && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <GovernmentOpportunities />
          </div>
        )}

        {!showSettings && view === 'calendar' && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <BDCalendar initialTab="board" />
          </div>
        )}

        {!showSettings && view === 'events' && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <BDCalendar initialTab="events" />
          </div>
        )}

        {!showSettings && view === 'insert-opportunity' && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <InsertGovOpportunity />
          </div>
        )}
      </div>

      {showWelcome && <WelcomeSnapshot onClose={() => setShowWelcome(false)} />}
    </div>
  );
}
